import { isRealCivilDay } from '@mfw/common';

import type { UiNotification } from '../schemas/notification.schema';
import type { DailyActivityPeriod } from '../store/useDailyActivityModalStore';
import type { MembershipCardSetupPayload } from '../store/useMembershipCardSetupStore';
import type { MembershipTouAcceptancePayload } from '../store/useMembershipTouAcceptanceStore';

// Shared by NotificationToast (the live toast) and NotificationsList (the
// notification-center panel) so the `data.type` switch lives in one place.
// Takes just `notification.data` (not the whole notification) — it's the
// only field either caller needs, and the toast doesn't carry a full
// UiNotification. Server-authored JSON — narrowed defensively, not trusted.
//
// Both callers also need to know whether a row is clickable BEFORE they render
// it, and each used to answer that with its own hand-written list of types.
// The lists drifted (ana--1 finding 7): the panel never listed
// `membership_tou_updated`, so the only entry point left to a client whose
// billing was paused for an unaccepted agreement did nothing. Resolving and
// asking "is this actionable" now run through the same narrowing below, so a
// new type cannot be handled here and stay inert there.

type NotificationTarget =
  | {
      kind: 'dailyActivity';
      period: DailyActivityPeriod;
      date: string;
      clientId?: string;
    }
  | { kind: 'membershipCardSetup'; payload: MembershipCardSetupPayload }
  | {
      kind: 'membershipTouAcceptance';
      payload: MembershipTouAcceptancePayload;
    }
  // a trainer put something on the client's calendar — opens that day
  | { kind: 'scheduleDay'; date: string }
  // a failed/suspended card charge — the payer's own card list: `personal`,
  // or the paying organization's id
  | { kind: 'accountCards'; scope: string };

function resolveNotificationTarget(
  data: UiNotification['data'],
): NotificationTarget | null {
  if (!data || typeof data !== 'object') return null;

  if (
    data.type === 'daily_activity_reminder' &&
    (data.period === 'morning' || data.period === 'evening') &&
    typeof data.date === 'string'
  ) {
    return { kind: 'dailyActivity', period: data.period, date: data.date };
  }

  if (
    data.type === 'daily_activity_shared' &&
    typeof data.clientId === 'string' &&
    typeof data.date === 'string'
  ) {
    const period =
      data.period === 'morning' || data.period === 'evening'
        ? data.period
        : 'morning';
    return {
      kind: 'dailyActivity',
      period,
      date: data.date,
      clientId: data.clientId,
    };
  }

  if (
    data.type === 'membership_card_setup_required' &&
    typeof data.userMembershipId === 'string' &&
    typeof data.organizationId === 'string' &&
    typeof data.clientId === 'string'
  ) {
    return {
      kind: 'membershipCardSetup',
      payload: {
        userMembershipId: data.userMembershipId,
        organizationId: data.organizationId,
        clientId: data.clientId,
      },
    };
  }

  if (
    data.type === 'membership_tou_updated' &&
    typeof data.userMembershipId === 'string' &&
    typeof data.organizationId === 'string' &&
    typeof data.clientId === 'string'
  ) {
    return {
      kind: 'membershipTouAcceptance',
      payload: {
        userMembershipId: data.userMembershipId,
        organizationId: data.organizationId,
        clientId: data.clientId,
      },
    };
  }

  if (
    data.type === 'schedule_event_added' &&
    typeof data.date === 'string' &&
    isRealCivilDay(data.date)
  ) {
    return { kind: 'scheduleDay', date: data.date };
  }

  if (data.type === 'billing_payment_problem') {
    return {
      kind: 'accountCards',
      scope:
        typeof data.payerOrganizationId === 'string'
          ? data.payerOrganizationId
          : 'personal',
    };
  }

  return null;
}

/**
 * Whether clicking this notification would open anything — the one predicate
 * both the toast and the notification centre render against.
 */
export function isActionableNotification(
  data: UiNotification['data'],
): boolean {
  return resolveNotificationTarget(data) !== null;
}

export function openNotificationTarget(
  data: UiNotification['data'],
  openDailyActivityModal: (
    period: DailyActivityPeriod,
    date: string,
    clientId?: string,
  ) => void,
  openMembershipCardSetup?: (payload: MembershipCardSetupPayload) => void,
  openMembershipTouAcceptance?: (
    payload: MembershipTouAcceptancePayload,
  ) => void,
  openAccountCards?: (scope: string) => void,
  openScheduleDay?: (date: string) => void,
): void {
  const target = resolveNotificationTarget(data);
  if (!target) return;

  if (target.kind === 'dailyActivity') {
    openDailyActivityModal(target.period, target.date, target.clientId);
    return;
  }

  if (target.kind === 'membershipCardSetup') {
    openMembershipCardSetup?.(target.payload);
    return;
  }

  if (target.kind === 'scheduleDay') {
    openScheduleDay?.(target.date);
    return;
  }

  if (target.kind === 'accountCards') {
    openAccountCards?.(target.scope);
    return;
  }

  openMembershipTouAcceptance?.(target.payload);
}
